/**
 * Theme Toggle Component
 * Allows users to switch between light, dark, and system theme preferences
 */

import React from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme, ThemePreference } from '@/hooks/use-theme';
import { cn } from '@/lib/utils';

interface ThemeToggleProps {
  /** Show as compact icon-only button */
  compact?: boolean;
  /** Additional class names */
  className?: string;
}

export function ThemeToggle({ compact = false, className }: ThemeToggleProps) {
  const { theme, preference, setPreference, toggleTheme } = useTheme();

  const options: { value: ThemePreference; label: string; icon: React.ReactNode }[] = [
    { value: 'light', label: 'Light', icon: <Sun className="h-4 w-4" /> },
    { value: 'dark', label: 'Dark', icon: <Moon className="h-4 w-4" /> },
    { value: 'system', label: 'System', icon: <Monitor className="h-4 w-4" /> },
  ];

  // Compact mode: just a toggle button
  if (compact) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className={cn(
          'h-9 w-9 rounded-full transition-colors',
          className
        )}
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {theme === 'dark' ? (
          <Sun className="h-4 w-4" />
        ) : (
          <Moon className="h-4 w-4" />
        )}
        <span className="sr-only">Toggle theme</span>
      </Button>
    );
  }

  // Full dropdown with all options
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'gap-2 transition-colors',
            className
          )}
        >
          {theme === 'dark' ? (
            <Moon className="h-4 w-4" />
          ) : (
            <Sun className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">
            {preference === 'system' ? 'System' : preference === 'dark' ? 'Dark' : 'Light'}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => setPreference(option.value)}
            className={cn(
              'gap-2 cursor-pointer',
              preference === option.value && 'bg-accent'
            )}
          >
            {option.icon}
            {option.label}
            {preference === option.value && (
              <span className="ml-auto text-xs text-muted-foreground">Active</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Inline theme toggle for sidebars
 * Shows current mode with a simple toggle button
 */
export function ThemeToggleInline({ className, transparent = false }: { className?: string; transparent?: boolean }) {
  const { preference, setPreference } = useTheme();

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="text-sm text-muted-foreground">Theme:</span>
      <div className={cn(
        'flex items-center gap-1 rounded-full p-1',
        transparent ? 'bg-transparent' : 'bg-muted'
      )}>
        <button
          onClick={() => setPreference('light')}
          className={cn(
            'p-1.5 rounded-full transition-colors',
            preference === 'light' && (transparent ? 'bg-muted/50' : 'bg-background shadow-sm')
          )}
          title="Light mode"
        >
          <Sun className={cn('h-3.5 w-3.5', preference === 'light' ? 'text-foreground' : 'text-muted-foreground')} />
        </button>
        <button
          onClick={() => setPreference('dark')}
          className={cn(
            'p-1.5 rounded-full transition-colors',
            preference === 'dark' && (transparent ? 'bg-muted/50' : 'bg-background shadow-sm')
          )}
          title="Dark mode"
        >
          <Moon className={cn('h-3.5 w-3.5', preference === 'dark' ? 'text-foreground' : 'text-muted-foreground')} />
        </button>
        <button
          onClick={() => setPreference('system')}
          className={cn(
            'p-1.5 rounded-full transition-colors',
            preference === 'system' && (transparent ? 'bg-muted/50' : 'bg-background shadow-sm')
          )}
          title="System preference"
        >
          <Monitor className={cn('h-3.5 w-3.5', preference === 'system' ? 'text-foreground' : 'text-muted-foreground')} />
        </button>
      </div>
    </div>
  );
}
